import { useState } from 'react';
import DataTable from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import SearchBar from '../../components/SearchBar';
import { useToast } from '../../context/ToastContext';
import { asStr, rowKey, diffCoords, CoordInput } from './inline';
import { useLocations, useBulkLocations } from '../../hooks/useLocation';

const LIMIT = 20;

const PLACE_TYPES = ['place', 'city', 'state', 'country'];

const MISSING_FILTERS = [
  { key: 'missing', label: 'Missing coordinates' },
  { key: 'has', label: 'Has coordinates' },
  { key: 'all', label: 'All' },
];

// filter -> `missing` query param ('all' omits it)
const missingParam = (f) => (f === 'missing' ? true : f === 'has' ? false : undefined);

export default function LocationModerationPage() {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [type, setType] = useState(''); // place_type filter ('' = all)
  const [missing, setMissing] = useState('missing'); // backlog first
  const [edits, setEdits] = useState(() => new Map()); // rowKey -> { lat, lon }

  const { data, isLoading, error } = useLocations({
    page,
    limit: LIMIT,
    search,
    type: type || undefined,
    missing: missingParam(missing),
  });
  const rows = data?.data || [];
  const total = data?.total;

  const bulkM = useBulkLocations({
    onSuccess: (res) => {
      const s = res?.data || {};
      toast.success(`Updated ${s.updated ?? 0}${s.failed ? ` · ${s.failed} failed` : ''}`);
      setEdits(new Map());
    },
    onError: (e) => toast.error(e.message || 'Bulk save failed'),
  });

  const resetEdits = () => setEdits(new Map());
  const changeSearch = (v) => { setSearch(v); setPage(1); resetEdits(); };
  const changeType = (v) => { setType(v); setPage(1); resetEdits(); };
  const changeMissing = (key) => { setMissing(key); setPage(1); resetEdits(); };
  const changePage = (p) => { setPage(p); resetEdits(); };

  const valFor = (row, field) => edits.get(rowKey(row))?.[field] ?? asStr(row[field]);
  const setField = (row, field, value) =>
    setEdits((prev) => {
      const key = rowKey(row);
      const next = new Map(prev);
      next.set(key, { ...(next.get(key) || {}), [field]: value });
      return next;
    });

  // Assemble the bulk payload — location items carry { type, id, lat?, lon? }.
  const buildBatch = () => {
    const items = [];
    const invalidRows = [];
    for (const row of rows) {
      const cur = { lat: valFor(row, 'lat'), lon: valFor(row, 'lon') };
      const { changed, errors } = diffCoords(cur, row);
      if (errors.length) invalidRows.push(row.name || `#${row.id}`);
      else if (Object.keys(changed).length) items.push({ type: row.place_type, id: row.id, ...changed });
    }
    return { items, invalidRows };
  };

  const { items, invalidRows } = buildBatch();

  const submit = () => {
    if (invalidRows.length) {
      toast.error(`Fix out-of-range coordinates for: ${invalidRows.join(', ')}`);
      return;
    }
    if (!items.length) return;
    bulkM.mutate(items);
  };

  const columns = [
    { key: 'id', header: 'ID' },
    {
      key: 'place_type',
      header: 'Type',
      render: (r) =>
        r.place_type ? <span className="badge">{r.place_type}</span> : <span className="muted">—</span>,
    },
    { key: 'name', header: 'Name', render: (r) => r.name || '—' },
    {
      key: 'lat',
      header: 'Lat',
      render: (r) => (
        <CoordInput
          value={valFor(r, 'lat')}
          onChange={(v) => setField(r, 'lat', v)}
          disabled={bulkM.isPending}
          lo={-90}
          hi={90}
        />
      ),
    },
    {
      key: 'lon',
      header: 'Lon',
      render: (r) => (
        <CoordInput
          value={valFor(r, 'lon')}
          onChange={(v) => setField(r, 'lon', v)}
          disabled={bulkM.isPending}
          lo={-180}
          hi={180}
        />
      ),
    },
  ];

  const saveBtn = (
    <button className="btn btn--primary" disabled={bulkM.isPending || items.length === 0} onClick={submit}>
      {bulkM.isPending ? 'Saving…' : `Save changes (${items.length})`}
    </button>
  );

  return (
    <div className="page">
      <div className="page__header"><h1>Location Moderation</h1></div>

      <div className="page__toolbar page__toolbar--row">
        <SearchBar value={search} onSearch={changeSearch} placeholder="Search by name…" />
        <select
          className="toolbar-select"
          value={type}
          onChange={(e) => changeType(e.target.value)}
          aria-label="Filter by type"
        >
          <option value="">All types</option>
          {PLACE_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <div className="seg">
          {MISSING_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`seg__btn${missing === f.key ? ' seg__btn--active' : ''}`}
              onClick={() => changeMissing(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        error={error}
        emptyText="No locations found."
      />

      {data && total != null && (
        <Pagination page={page} limit={LIMIT} total={total} onPage={changePage} />
      )}

      {rows.length > 0 && <div className="page__footer-actions">{saveBtn}</div>}
    </div>
  );
}
